import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Search, 
  Users, 
  Car, 
  FileText, 
  AlertTriangle, 
  X,
  ExternalLink
} from 'lucide-react';
import { apiService } from '../../services/api';

export const PesquisaGlobal = () => {
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && 
        !searchRef.current.contains(event.target) &&
        resultsRef.current &&
        !resultsRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (termo.length >= 2) {
        realizarPesquisa();
      } else {
        setResultados([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [termo]);

  const realizarPesquisa = async () => {
    setLoading(true);
    try {
      const response = await apiService.pesquisaGlobal(termo);
      setResultados(response.results || []);
      setShowResults(true);
    } catch (error) {
      console.error('Erro na pesquisa:', error);
      setResultados([]);
    } finally {
      setLoading(false);
    }
  };

  const getIconoTipo = (tipo) => {
    switch (tipo) {
      case 'cliente':
        return <Users className="h-4 w-4 text-blue-500" />;
      case 'moto':
        return <Car className="h-4 w-4 text-green-500" />;
      case 'locacao':
        return <FileText className="h-4 w-4 text-purple-500" />;
      case 'cobranca':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      default:
        return <Search className="h-4 w-4 text-gray-500" />;
    }
  };

  const getCorTipo = (tipo) => {
    switch (tipo) {
      case 'cliente':
        return 'bg-blue-100 text-blue-800';
      case 'moto':
        return 'bg-green-100 text-green-800';
      case 'locacao':
        return 'bg-purple-100 text-purple-800';
      case 'cobranca':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const selecionarResultado = (resultado) => {
    navigate(resultado.url);
    setTermo('');
    setShowResults(false);
    setResultados([]);
  };

  const limparPesquisa = () => {
    setTermo('');
    setResultados([]);
    setShowResults(false);
  };

  return (
    <div className="relative" ref={searchRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          type="text"
          placeholder="Pesquisar no sistema..."
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          className="pl-10 pr-10 w-64 bg-white/90 backdrop-blur-sm"
          onFocus={() => termo.length >= 2 && setShowResults(true)}
        />
        {termo && (
          <button
            onClick={limparPesquisa}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>

      {showResults && (
        <div
          ref={resultsRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
        >
          {resultados.length > 0 ? (
            <div className="p-2">
              <div className="text-xs text-gray-500 px-2 py-1 border-b">
                {resultados.length} resultado(s) encontrado(s)
              </div>
              {resultados.map((resultado, index) => (
                <div
                  key={index}
                  onClick={() => selecionarResultado(resultado)}
                  className="flex items-center space-x-3 p-3 hover:bg-white/5 cursor-pointer rounded-md m-1 group"
                >
                  <div className="flex-shrink-0">
                    {getIconoTipo(resultado.tipo)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {resultado.titulo}
                      </p>
                      <Badge size="sm" className={getCorTipo(resultado.tipo)}>
                        {resultado.tipo}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {resultado.subtitulo}
                    </p>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <ExternalLink className="h-3 w-3 text-gray-400 group-hover:text-gray-600" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500">
              <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">
                {termo.length < 2 
                  ? 'Digite pelo menos 2 caracteres para pesquisar'
                  : 'Nenhum resultado encontrado'
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};